import {
  ARTICLE_TOKENS,
  SCROLL_MARGIN_CLASS
} from '@components/article/design-tokens';
import Paywall from '@components/paywall';
import { ClubSection } from '@components/predicted-lineups/ClubSection';
import type { PublishedLineup } from '@api-service/predicted-lineups';
import type { PremiumTeamSectionsState } from '@hooks/usePremiumTeamSections';
import { EMPTY_ARTICLE_BLOCK_DATA } from '@lib/article-blocks/hydrate';
import { findLineupForClub } from '@lib/predicted-lineups/compose';
import type {
  ClubDescriptor,
  ClubHeading
} from '@lib/predicted-lineups/compose';
import type { FlaggedPlayer } from '@lib/predicted-lineups/availability';
import type { PlayerFactsMap } from '@lib/predicted-lineups/player-facts';

const PAYWALL_ID = 'members-paywall';
const PAYWALL_SUBTITLE =
  'Pro and Ultra members see every club’s predicted lineup, team news, injuries and doubts.';
const PLACEHOLDER_LINE_WIDTHS = ['w-full', 'w-11/12', 'w-3/4'];

const buildPaywallTitle = (clubCount: number) =>
  `${clubCount} more ${clubCount === 1 ? 'club' : 'clubs'} for members`;

const LoadingWords = () => (
  <div
    className="my-6 space-y-3"
    data-cy="club-words-loading"
    aria-busy="true"
    aria-label="Loading team news"
  >
    {PLACEHOLDER_LINE_WIDTHS.map((width, index) => (
      <div
        key={index}
        className={`h-4 animate-pulse rounded bg-neutral-200 ${width}`}
      />
    ))}
  </div>
);

const LockedWords = () => (
  <div
    className="my-6 space-y-3 blur-[2px]"
    data-cy="club-words-locked"
    aria-hidden="true"
  >
    {PLACEHOLDER_LINE_WIDTHS.map((width, index) => (
      <div key={index} className={`h-4 rounded bg-neutral-200 ${width}`} />
    ))}
  </div>
);

const LockedClubOverlay = ({ clubName }: { clubName: string }) => (
  <div className="absolute inset-0 flex items-center justify-center p-4">
    <a
      href={`#${PAYWALL_ID}`}
      className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2.5 font-body text-sm font-bold text-neutral-800 no-underline shadow-lg ring-1 ring-black/5 hover:bg-neutral-50"
      data-cy="club-locked-cta"
    >
      <span aria-hidden="true">🔒</span>
      {clubName} lineup and team news for members
    </a>
  </div>
);

const LoadFailed = ({ retry }: { retry: () => void }) => (
  <div
    className={`my-6 rounded-[14px] border p-5 text-center ${ARTICLE_TOKENS.rule} ${ARTICLE_TOKENS.surfaceTint}`}
    data-cy="club-words-error"
    role="alert"
  >
    <p className={`m-0 mb-3 text-sm ${ARTICLE_TOKENS.body}`}>
      We could not load the team news.
    </p>
    <button
      type="button"
      onClick={retry}
      className="rounded-lg border-2 border-transparent bg-primary-600 px-3 py-[7px] font-open-sans text-base font-bold text-white transition-colors hover:bg-primary-800"
    >
      Try again
    </button>
  </div>
);

const NO_FLAGGED_PLAYERS: FlaggedPlayer[] = [];

type GatedClubsProps = {
  clubs: ClubDescriptor[];
  headings: ClubHeading[];
  lineups: PublishedLineup[];
  playerFacts: PlayerFactsMap;
  state: PremiumTeamSectionsState;
  returnTo: string;
};

/**
 * The clubs without a free section. Their headings render for everyone. Until
 * the premium read resolves for an entitled member, each pitch is a blank
 * LockedPitch rather than the eleven, so no names show; a visitor who cannot
 * read the clubs sees the paywall once, then each club blurred behind a lock.
 * The elevens stay in the page data, as they are on the public lineups API,
 * so this gate is for readers, not scrapers.
 */
export const GatedClubs = ({
  clubs,
  headings,
  lineups,
  playerFacts,
  state,
  returnTo
}: GatedClubsProps) => {
  if (clubs.length === 0) return null;

  const wordsFor = (teamShortName: string) =>
    state.status === 'ready'
      ? state.sections.find(
          (section) => section.teamShortName === teamShortName
        )
      : undefined;

  const isLocked =
    state.status !== 'ready' && state.status !== 'not-applicable';
  const fallback =
    state.status === 'loading' ? (
      <LoadingWords />
    ) : state.status === 'error' ? (
      <LoadFailed retry={state.retry} />
    ) : (
      <LockedWords />
    );

  return (
    <div data-cy="gated-clubs">
      {state.status === 'locked' && (
        <div
          id={PAYWALL_ID}
          className={`${SCROLL_MARGIN_CLASS} mt-14`}
          data-cy="club-paywall"
        >
          <Paywall
            variant="embedded"
            title={buildPaywallTitle(clubs.length)}
            subtitle={PAYWALL_SUBTITLE}
            returnTo={returnTo}
          />
        </div>
      )}
      {clubs.map((club, index) => (
        <ClubSection
          key={headings[index]?.id ?? club.teamShortName}
          club={club}
          heading={
            headings[index] ?? {
              id: club.teamShortName,
              text: club.teamShortName,
              teamShortName: club.teamShortName
            }
          }
          lineup={findLineupForClub(lineups, club.teamShortName)}
          playerFacts={playerFacts}
          flaggedPlayers={
            state.status === 'ready' ? state.flaggedPlayers : NO_FLAGGED_PLAYERS
          }
          words={wordsFor(club.teamShortName)}
          blockData={
            state.status === 'ready'
              ? state.blockData
              : EMPTY_ARTICLE_BLOCK_DATA
          }
          wordsFallback={fallback}
          isLocked={isLocked}
          lockedOverlay={
            state.status === 'locked' ? (
              <LockedClubOverlay
                clubName={club.teamName ?? club.teamShortName}
              />
            ) : undefined
          }
        />
      ))}
    </div>
  );
};
